// src/services/returnService.js
import ApiService from './api'

class ReturnService {
  /**
   * Create a new order return
   * @param {Object} returnData - Return data
   * @param {string} returnData.orderId - Order ID to return items from
   * @param {string} returnData.returnType - Type of return ('customer' | 'internal')
   * @param {string} returnData.refundMethod - Refund method ('cash' | 'exchange')
   * @param {Array} returnData.items - Array of items to return
   * @param {string} returnData.reasonType - Reason for return ('change_mind' | 'expired' | 'damage' | 'wrong_prep')
   * @returns {Promise<Object>} - API response
   */
  async createOrderReturn(returnData) {
    try {
      console.log('🔄 Creating order return:', returnData)
      
      // Validate required fields
      if (!returnData.orderId) {
        throw new Error('Order ID is required')
      }
      if (!returnData.items || returnData.items.length === 0) {
        throw new Error('At least one item must be selected for return')
      }
      
      const response = await ApiService.post('/order-returns', returnData)
      
      console.log('✅ Order return created successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error creating order return:', error)
      
      // Enhanced error handling with specific messages
      if (error.response?.status === 400) {
        const message = error.response?.data?.message || 'Invalid return data provided'
        throw new Error(message)
      } else if (error.response?.status === 404) {
        throw new Error('Order not found or cannot be returned')
      } else if (error.response?.status === 409) {
        throw new Error('Return already exists for this order')
      } else if (error.response?.status === 422) {
        throw new Error('Return quantity exceeds available quantity')
      } else if (error.message?.includes('Network Error')) {
        throw new Error('Network error. Please check your connection and try again')
      }
      
      throw error
    }
  }

  /**
   * Void an entire order (full cancellation, all items restocked, service charge zeroed)
   * @param {Object} voidData
   * @param {string} voidData.orderId
   * @param {string} voidData.refundMethod - 'cash' | 'exchange'
   * @param {string} [voidData.handledBy]  - Employee ID (optional)
   * @param {string} [voidData.note]
   */
  async voidOrder(voidData) {
    try {
      console.log('🚫 Voiding order:', voidData)

      if (!voidData.orderId) throw new Error('Order ID is required')
      if (!voidData.refundMethod) throw new Error('Refund method is required')

      const response = await ApiService.post('/order-returns/void', voidData)

      console.log('✅ Order voided successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error voiding order:', error)

      const msg = error.response?.data?.message || ''
      if (error.response?.status === 409) throw new Error('This order has already been voided')
      if (error.response?.status === 400) throw new Error(msg || 'Invalid void request')
      if (error.response?.status === 404) throw new Error('Order not found')
      if (error.response?.status === 403) throw new Error('Access denied')
      throw error
    }
  }

  /**
   * Get order return by ID
   * @param {string} returnId - Return ID
   * @returns {Promise<Object>} - API response
   */
  async getOrderReturn(returnId) {
    try {
      console.log('📋 Fetching order return:', returnId)
      
      const response = await ApiService.get(`/order-returns/${returnId}`)
      
      console.log('✅ Order return fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching order return:', error)
      throw error
    }
  }

  /**
   * Get all order returns with filtering
   * @param {Object} params - Query parameters for filtering
   * @returns {Promise<Object>} - API response
   */
  async getAllOrderReturns(params = {}) {
    try {
      console.log('📋 Fetching order returns with params:', params)
      
      const response = await ApiService.get('/order-returns', params)
      
      console.log('✅ Order returns fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching order returns:', error)
      
      // Enhanced error handling
      if (error.response?.status === 404) {
        // Return empty successful response for 404 (no returns found)
        console.log('📝 No returns found, returning empty array')
        return {
          success: true,
          data: [],
          message: 'No returns found'
        }
      } else if (error.response?.status === 403) {
        throw new Error('Access denied. You do not have permission to view returns')
      } else if (error.message?.includes('Network Error')) {
        throw new Error('Network error. Please check your connection and try again')
      }
      
      throw error
    }
  }

  /**
   * Update order return status
   * @param {string} returnId - Return ID
   * @param {Object} updateData - Data to update
   * @returns {Promise<Object>} - API response
   */
  async updateOrderReturn(returnId, updateData) {
    try {
      console.log('🔄 Updating order return:', returnId, updateData)
      
      const response = await ApiService.put(`/order-returns/${returnId}`, updateData)
      
      console.log('✅ Order return updated successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error updating order return:', error)
      throw error
    }
  }

  /**
   * Cancel order return
   * @param {string} returnId - Return ID
   * @returns {Promise<Object>} - API response
   */
  async cancelOrderReturn(returnId) {
    try {
      console.log('❌ Cancelling order return:', returnId)
      
      const response = await ApiService.delete(`/order-returns/${returnId}`)
      
      console.log('✅ Order return cancelled successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error cancelling order return:', error)
      throw error
    }
  }

  /**
   * Get return statistics
   * @param {Object} params - Query parameters for filtering
   * @returns {Promise<Object>} - API response
   */
  async getReturnStatistics(params = {}) {
    try {
      console.log('📊 Fetching return statistics with params:', params)
      
      const response = await ApiService.get('/order-returns/statistics', params)
      
      console.log('✅ Return statistics fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching return statistics:', error)
      throw error
    }
  }

  /**
   * Validate return eligibility for an order
   * @param {string} orderId - Order ID to check
   * @returns {Promise<Object>} - API response
   */
  async validateReturnEligibility(orderId) {
    try {
      console.log('🔍 Validating return eligibility for order:', orderId)
      
      const response = await ApiService.get(`/order-returns/validate/${orderId}`)
      
      console.log('✅ Return eligibility validated successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error validating return eligibility:', error)
      throw error
    }
  }

  /**
   * Get return reasons
   * @returns {Array} - Available return reasons
   */
  getReturnReasons() {
    return [
      { value: 'change_mind', label: 'Change of Mind', description: 'Customer changed their mind' },
      { value: 'expired', label: 'Expired Product', description: 'Product has expired' },
      { value: 'damage', label: 'Damaged Product', description: 'Product is damaged' },
      { value: 'wrong_prep', label: 'Wrong Preparation', description: 'Product was prepared incorrectly' }
    ]
  }

  /**
   * Get return types
   * @returns {Array} - Available return types
   */
  getReturnTypes() {
    return [
      { value: 'customer', label: 'Customer Return', description: 'Customer initiated return' },
      { value: 'internal', label: 'Internal Return', description: 'Staff initiated return' }
    ]
  }

  /**
   * Get refund methods
   * @returns {Array} - Available refund methods
   */
  getRefundMethods() {
    return [
      { value: 'cash', label: 'Cash Refund', description: 'Refund in cash' },
      { value: 'exchange', label: 'Exchange', description: 'Exchange for other items' }
    ]
  }
}

// Create and export a singleton instance
const returnService = new ReturnService()
export default returnService
